import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { kioskApi } from '../../api/kiosk.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { ApiError } from '../../api/client.js';
import { Card } from '../../components/ui/Card.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { t } from '../../lib/i18n.js';

export function ConsentBanner({ onGranted }) {
  const { user } = useAuth();
  const principalStore = user && user.storeId;
  const [storeCode, setStoreCode] = useState('');
  const [voice, setVoice] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const purposes = ['ai_chat'];
      if (voice) purposes.push('voice_chat');
      if (analytics) purposes.push('analytics');
      const body = { purposes };
      if (!principalStore && storeCode.trim()) body.storeCode = storeCode.trim().toLowerCase();
      return kioskApi.grant(body);
    },
    onSuccess: (data) => {
      onGranted(data);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <Card>
        <header className="mb-5">
          <h1 className="text-2xl font-semibold text-ink">Benvenuto</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Per iniziare la conversazione con l'assistente serve il tuo consenso. I dati raccolti sono
            anonimi: nessun nome o contatto è memorizzato, e la sessione scade automaticamente dopo
            un'ora di inattività.
          </p>
        </header>

        {!principalStore && (
          <div className="mb-4">
            <Input
              label="Codice del negozio"
              placeholder="es. pa-centro"
              value={storeCode}
              onChange={(e) => setStoreCode(e.target.value)}
              hint="Riferimento del punto vendita configurato in admin."
            />
          </div>
        )}

        <div className="mb-5 flex flex-col gap-2">
          <p className="text-sm font-medium text-ink">Finalità del consenso</p>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" checked readOnly className="mt-0.5" />
            <span>
              <span className="font-medium">Chat AI</span> (sempre richiesta)
              <span className="block text-xs text-ink-muted">L'assistente leggerà le tue domande per consigliarti prodotti.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" checked={voice} onChange={(e) => setVoice(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium">Conversazione vocale</span>
              <span className="block text-xs text-ink-muted">Microfono e altoparlante per parlare a voce con l'assistente.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium">Analisi anonime</span>
              <span className="block text-xs text-ink-muted">Statistiche aggregate per migliorare l'assistente. Nessun dato personale.</span>
            </span>
          </label>
        </div>

        <Button
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!principalStore && !storeCode.trim()}
          size="lg"
          className="w-full"
        >
          Accetto e inizio
        </Button>

        <p className="mt-3 text-center text-xs text-ink-muted">
          Per maggiori informazioni chiedi al personale del negozio.
        </p>
      </Card>
    </div>
  );
}
